import type { ReactNode, TableHTMLAttributes } from "react";
import { type To, useNavigate } from "react-router";
import type { DisplayFraction } from "@/types/generated/openapi";
import { cn } from "@/utils/classnames";
import { getFractionInteger, getFractionWithoutInteger } from "@/utils/fraction";
import cls from "./Table.module.css";

interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  variant?: "default" | "information";
}

export function Table({ variant = "default", children, className, ...props }: TableProps) {
  return (
    <table className={cn(cls.table, cls[`${variant}Variant`], className)} {...props}>
      {children}
    </table>
  );
}

Table.Header = Header;
Table.HeaderCell = HeaderCell;
Table.Body = Body;
Table.Row = Row;
Table.ClickRow = ClickRow;
Table.TotalRow = TotalRow;
Table.Cell = Cell;
Table.NumberCell = NumberCell;
Table.DisplayFractionCells = DisplayFractionCells;
Table.FractionAlignedNumberCells = FractionAlignedNumberCells;

function Header({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <thead>
      <tr className={className}>{children}</tr>
    </thead>
  );
}

function HeaderCell({
  children,
  scope,
  span,
  numberWidth,
  className,
}: {
  children?: ReactNode;
  scope?: "col" | "row";
  span?: number;
  numberWidth?: boolean;
  className?: string;
}) {
  return (
    <th
      scope={scope || "col"}
      rowSpan={scope === "row" ? span : undefined}
      colSpan={scope !== "row" ? span : undefined}
      className={cn(numberWidth && cls.numberWidth, className)}
    >
      {children}
    </th>
  );
}

function Body({ children, className }: { children: ReactNode; className?: string }) {
  return <tbody className={className}>{children}</tbody>;
}

function Row({ id, children, to, className }: { id?: string; children: ReactNode; to?: To; className?: string }) {
  const navigate = useNavigate();

  if (to) {
    return (
      <tr id={id} className={cn(cls.rowLink, className)} onClick={() => void navigate(to)}>
        {children}
      </tr>
    );
  } else {
    return (
      <tr id={id} className={className}>
        {children}
      </tr>
    );
  }
}

function ClickRow({
  id,
  children,
  onClick,
  downloadIcon,
  active,
  className,
}: {
  id?: string;
  children: ReactNode;
  onClick: () => void;
  downloadIcon?: boolean;
  active?: boolean;
  className?: string;
}) {
  return (
    <tr
      id={id}
      className={cn(cls.rowLink, downloadIcon && cls.downloadIcon, active && cls.active, className)}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

function TotalRow({ children, className }: { children?: ReactNode; className?: string }) {
  return <tr className={cn(cls.rowTotal, className)}>{children}</tr>;
}

function Cell({ children, colSpan, className }: { children?: ReactNode; colSpan?: number; className?: string }) {
  return (
    <td colSpan={colSpan} className={className}>
      {children}
    </td>
  );
}

function NumberCell({ children, colSpan, className }: { children?: ReactNode; colSpan?: number; className?: string }) {
  return (
    <td colSpan={colSpan} className={cn(cls.numberCell, className)}>
      {children}
    </td>
  );
}

function DisplayFractionCells({ children, className }: { children: DisplayFraction | undefined; className?: string }) {
  return (
    <>
      <td className={cn(cls.integerCell, "font-number", className)}>{children && getFractionInteger(children)}</td>
      <td className={cn(cls.fractionCell, "font-number", className)}>
        {children && getFractionWithoutInteger(children)}
      </td>
    </>
  );
}

function FractionAlignedNumberCells({ children, className }: { children: number; className?: string }) {
  return (
    <>
      <td className={cn(cls.integerCell, "font-number", className)}>{children}</td>
      <td className={cn(cls.fractionCell, "font-number", className)}></td>
    </>
  );
}
